import { useMutation, useQuery } from "@tanstack/react-query";
import { useAdminApplication, useAdminSession } from "../../app/providers";
import type { Schemas } from "../workspace/api.generated";
import * as integrationApi from "./api";
import type { ProviderConfigurationQuery } from "./api";

export const integrationKeys = {
  all: (epoch: number) => ["admin", epoch, "integrations"] as const,
  lists: (epoch: number) => [...integrationKeys.all(epoch), "lists"] as const,
  list: (epoch: number, query: ProviderConfigurationQuery) => [...integrationKeys.lists(epoch), query] as const,
  detail: (epoch: number, configurationId: number) => [...integrationKeys.all(epoch), "detail", configurationId] as const,
  history: (epoch: number, configurationId: number) => [...integrationKeys.all(epoch), "credential-history", configurationId] as const,
};

export function useProviderConfiguration(configurationId: number) {
  const { apiClient } = useAdminApplication(); const session = useAdminSession();
  return useQuery({ queryKey: integrationKeys.detail(session.sessionEpoch, configurationId), queryFn: ({ signal }) => integrationApi.getProviderConfiguration(apiClient, configurationId, signal), staleTime: 0 });
}

export function useProviderConfigurations(query: ProviderConfigurationQuery) {
  const { apiClient } = useAdminApplication(); const session = useAdminSession();
  return useQuery({ queryKey: integrationKeys.list(session.sessionEpoch, query), queryFn: ({ signal }) => integrationApi.getProviderConfigurations(apiClient, query, signal), staleTime: 0 });
}
export function useProviderCredentialHistory(configurationId?: number) {
  const { apiClient } = useAdminApplication(); const session = useAdminSession();
  return useQuery({ queryKey: integrationKeys.history(session.sessionEpoch, configurationId ?? 0), queryFn: ({ signal }) => integrationApi.getProviderCredentialHistory(apiClient, configurationId!, signal), enabled: configurationId !== undefined, staleTime: 0 });
}

export type IntegrationMutation =
  | { readonly kind: "create"; readonly body: Schemas["CreateProviderConfigurationRequest"] }
  | { readonly kind: "credential"; readonly id: number; readonly body: Schemas["RotateProviderCredentialRequest"] }
  | { readonly kind: "connection-test"; readonly id: number }
  | { readonly kind: "activate"; readonly id: number };

export function useIntegrationMutation() {
  const { apiClient, queryClient, sessionLifecycle } = useAdminApplication(); const session = useAdminSession();
  return useMutation({
    mutationFn: async (input: IntegrationMutation) => {
      const request = sessionLifecycle.createRequestController();
      try {
        if (input.kind === "create") return integrationApi.createProviderConfiguration(apiClient, input.body, request.controller.signal);
        if (input.kind === "credential") return integrationApi.rotateProviderCredential(apiClient, input.id, input.body, request.controller.signal);
        if (input.kind === "connection-test") return integrationApi.testProviderConnection(apiClient, input.id, request.controller.signal);
        return integrationApi.activateProviderConfiguration(apiClient, input.id, request.controller.signal);
      } finally { request.release(); }
    },
    onSuccess: async (_result, input) => {
      await queryClient.invalidateQueries({ queryKey: integrationKeys.lists(session.sessionEpoch) });
      if (input.kind !== "create") await queryClient.invalidateQueries({ queryKey: integrationKeys.detail(session.sessionEpoch, input.id) });
      if (input.kind === "credential") await queryClient.invalidateQueries({ queryKey: integrationKeys.history(session.sessionEpoch, input.id) });
    },
  });
}
